"use client";

import { Play, ListVideo } from "lucide-react";
import { useState } from "react";
import type { PortraitVideoCardProps } from "./PortraitVideoCard.types";
import VideoCardSkeleton from "../../skeletons/VideoCardSkeleton/VideoCardSkeleton";
import PrivateCdnImage from "@/asset-delivery/components/PrivateCdnImage";

import "./PortraitVideoCard.css";

const PortraitVideoCard = ({
  title,
  thumbnail,
  thumbnailTarget,
  thumbnailKind = "thumbnail",
  thumbnailFieldName = "thumbnail",
  showPlaylistIcon = false,
  onPlaylistClick,
  className = "",
  onClick,
  iconSize = 28,
  isPlaceholder = false,
}: PortraitVideoCardProps) => {
  const [isTitleExpanded, setIsTitleExpanded] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  const toggleTitle = () => setIsTitleExpanded((prev) => !prev);

  if (isPlaceholder) {
    return (
      <div className={`portrait-card-container ${className} placeholder`}>
        <div className="portrait-thumbnail placeholder-bg">
          <div className="placeholder-glow-circle">
            <Play size={iconSize * 1.5} />
          </div>
          <div className="placeholder-message">
            <p className="placeholder-title">Coming Soon</p>
            <p className="placeholder-subtitle">This space is becoming sacred</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`portrait-card-container ${className}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick?.();
        }
      }}
    >
      <div className="portrait-thumbnail">
        {!isLoaded && <VideoCardSkeleton variant="portrait" />}

        {thumbnail && thumbnailTarget ? (
          <PrivateCdnImage
            src={thumbnail}
            placeholderSrc={null}
            alt={title || "Video thumbnail"}
            className={`portrait-video-element ${isLoaded ? "loaded" : "loading"}`}
            target={thumbnailTarget}
            kind={thumbnailKind as any}
            fieldName={thumbnailFieldName as any}
            rootMargin="900px"
            threshold={0.01}
            failOpen
            imgLoading="eager"
            fetchPriority="high"
            onLoad={() => setIsLoaded(true)}
            onError={() => setIsLoaded(true)} // avoid skeleton hanging forever
          />
        ) : thumbnail ? (
          <img
            src={thumbnail}
            alt={title || "Video thumbnail"}
            className={`portrait-video-element ${isLoaded ? "loaded" : "loading"}`}
            loading="eager"
            decoding="async"
            draggable={false}
            onLoad={() => setIsLoaded(true)}
            onError={() => setIsLoaded(true)}
          />
        ) : (
          <div className="portrait-video-element loading" />
        )}

        {title && (
          <div
            className={`portrait-title-overlay ${
              isTitleExpanded ? "expanded" : "collapsed"
            }`}
            onClick={(e) => {
              e.stopPropagation();
              toggleTitle();
            }}
            title={isTitleExpanded ? "Hide Title" : "Show Full Title"}
          >
            <p>{title}</p>
          </div>
        )}
      </div>

      {showPlaylistIcon && onPlaylistClick && (
        <button
          className="portrait-playlist-icon"
          onClick={(e) => {
            e.stopPropagation();
            onPlaylistClick();
          }}
          title="Go to Playlist"
        >
          <ListVideo size={iconSize} />
        </button>
      )}
    </div>
  );
};

export default PortraitVideoCard;
